import { createContext, useContext, useState, useEffect, ReactNode } from "react";

interface CelebrationSettings {
  soundEnabled: boolean;
  hapticEnabled: boolean;
  setSoundEnabled: (enabled: boolean) => void;
  setHapticEnabled: (enabled: boolean) => void;
}

const CelebrationSettingsContext = createContext<CelebrationSettings | undefined>(undefined);

const STORAGE_KEY = "castready-celebration-settings";

export const CelebrationSettingsProvider = ({ children }: { children: ReactNode }) => {
  const [soundEnabled, setSoundEnabledState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.soundEnabled ?? true;
      }
    } catch {}
    return true;
  });

  const [hapticEnabled, setHapticEnabledState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.hapticEnabled ?? true;
      }
    } catch {}
    return true;
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ soundEnabled, hapticEnabled }));
    } catch {}
  }, [soundEnabled, hapticEnabled]);

  const setSoundEnabled = (enabled: boolean) => setSoundEnabledState(enabled);
  const setHapticEnabled = (enabled: boolean) => setHapticEnabledState(enabled);

  return (
    <CelebrationSettingsContext.Provider value={{ soundEnabled, hapticEnabled, setSoundEnabled, setHapticEnabled }}>
      {children}
    </CelebrationSettingsContext.Provider>
  );
};

export const useCelebrationSettings = () => {
  const context = useContext(CelebrationSettingsContext);
  if (!context) {
    // Return defaults if used outside provider
    return {
      soundEnabled: true,
      hapticEnabled: true,
      setSoundEnabled: () => {},
      setHapticEnabled: () => {},
    };
  }
  return context;
};
